import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Trophy, Star, Target, TrendingUp, Loader2, Medal, ChevronRight, Zap } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function ProBenchmarks({ childId, childData }) {
  const [loading, setLoading] = useState(false);
  const [sports, setSports] = useState([]);
  const [selectedSport, setSelectedSport] = useState('football');
  const [comparison, setComparison] = useState(null);
  const [pathway, setPathway] = useState(null);

  useEffect(() => {
    fetchSports();
  }, []);

  const fetchSports = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/benchmarks/sports`, { headers });
      setSports(res.data.sports);
    } catch (err) {
      console.error('Failed to fetch sports');
    }
  };

  const compare = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [compRes, pathRes] = await Promise.all([
        axios.post(`${API}/benchmarks/compare/${childId}?sport=${selectedSport}`, {}, { headers }),
        axios.get(`${API}/benchmarks/pathway/${childId}?sport=${selectedSport}`, { headers })
      ]);
      setComparison(compRes.data);
      setPathway(pathRes.data);
    } catch (err) {
      toast.error('Failed to fetch benchmark comparison');
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level) => {
    if (level.includes('Elite')) return 'text-yellow-400';
    if (level.includes('Academy')) return 'text-[#02b5ff]';
    if (level.includes('Development')) return 'text-green-500';
    if (level.includes('Club')) return 'text-purple-400';
    return 'text-zinc-400';
  };

  const getLevelBg = (level) => {
    if (level.includes('Elite')) return 'bg-yellow-500/20 border-yellow-500/50';
    if (level.includes('Academy')) return 'bg-[#02b5ff]/20 border-[#02b5ff]/50';
    if (level.includes('Development')) return 'bg-green-500/20 border-green-500/50';
    if (level.includes('Club')) return 'bg-purple-500/20 border-purple-500/50';
    return 'bg-zinc-800/50 border-zinc-700';
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid="pro-benchmarks">
      <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
        <Trophy className="h-5 w-5 text-yellow-500" />
        Pro Academy Benchmarks
      </h3>

      <div className="flex gap-2 mb-4">
        <select
          value={selectedSport}
          onChange={(e) => setSelectedSport(e.target.value)}
          className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
        >
          {sports.map((sport) => (
            <option key={sport.id} value={sport.id}>{sport.name}</option>
          ))}
        </select>
        <Button
          onClick={compare}
          disabled={loading}
          className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-semibold rounded-lg"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4 mr-1" />}
          Compare
        </Button>
      </div>

      {comparison && (
        <div className="space-y-4">
          {/* Overall Score */}
          <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
            <div className="text-sm text-zinc-400 mb-1">Overall Academy Score</div>
            <div className={`text-4xl font-bold ${
              comparison.overall_score >= 90 ? 'text-yellow-400' :
              comparison.overall_score >= 75 ? 'text-[#02b5ff]' :
              comparison.overall_score >= 60 ? 'text-green-500' : 'text-zinc-400'
            }`}>
              {comparison.overall_score}%
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              of Elite {selectedSport.replace('_', ' ')} Academy Level
            </div>
          </div>

          {/* Test Comparisons */}
          {comparison.comparisons?.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm text-zinc-400 mb-2">Test Results vs Academy Standards</div>
              {comparison.comparisons.map((comp, i) => (
                <div 
                  key={i} 
                  className={`rounded-lg p-3 border ${getLevelBg(comp.level)}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{comp.emoji}</span>
                        <span className="font-semibold text-white">{comp.test_name}</span>
                      </div>
                      <div className="text-xs text-zinc-400">
                        Your result: <span className="text-white">{comp.athlete_value}{comp.unit}</span>
                        {' '}• Elite: <span className="text-yellow-400">{comp.benchmark_value}{comp.unit}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-xl font-bold ${getLevelColor(comp.level)}`}>
                        {comp.percentage_of_elite}%
                      </div>
                      <div className={`text-xs ${getLevelColor(comp.level)}`}>{comp.level}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {comparison.comparisons?.length === 0 && (
            <div className="text-center py-6 text-zinc-500">
              <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No matching test results found</p>
              <p className="text-xs">Record athletic tests to compare with academy benchmarks</p>
            </div>
          )}

          {/* Development Pathway */}
          {pathway?.development_areas?.length > 0 && (
            <div className="bg-zinc-800/30 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[#02b5ff]" />
                Development Focus Areas
              </h4>
              <div className="space-y-2">
                {pathway.development_areas.slice(0, 3).map((area, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      area.priority === 'high' ? 'bg-[#ff025f]' :
                      area.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                    }`} />
                    <span className="text-sm text-zinc-300">{area.test}</span>
                    <ChevronRight className="h-3 w-3 text-zinc-600" />
                    <span className="text-xs text-zinc-500">{area.recommendation}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!comparison && (
        <div className="text-center py-6 text-zinc-500">
          <Medal className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Select a sport and click Compare</p>
          <p className="text-xs">See how you measure up against elite academy standards</p>
        </div>
      )}
    </div>
  );
}
